/**
 * Guesses the country to pre-select in the phone field for a user we know nothing about yet.
 *
 * Swonkie used to hardcode 'PT' here, which stopped making sense once we started selling
 * outside Portugal: a Brazilian signing up was shown a +351 flag and had to find Brazil in a
 * 245-entry dropdown on the very first screen of onboarding.
 *
 * We deliberately do NOT use the user's IP or user agent:
 *   - the user agent carries no geographic information at all;
 *   - IP would need a server-side lookup, is personal data under GDPR, and is wrong for
 *     anyone behind a VPN.
 * The browser timezone is a stronger signal than either, costs nothing, needs no network call
 * and survives VPNs, so that is what we lead with.
 *
 * We also do not read `me.country`: every native user is created with country PT hardcoded
 * (`provisionNativeUser` in swonkie-nest) and there is no mutation to change it, so it is a
 * constant, not a signal. Trusting it would override a correct timezone guess.
 */
import { CountryCode, isSupportedCountry } from 'libphonenumber-js/max';
import { TIMEZONE_COUNTRY } from './timezone-country';

/** Last resort when neither the timezone nor the locale tells us anything. */
export const DEFAULT_PHONE_COUNTRY: CountryCode = 'PT';

/**
 * zone.tab lists a few countries libphonenumber has no calling code for (AQ, GS, TF, UM, PN).
 * Returning one of those would make `getCountryCallingCode` throw, so they are filtered out.
 */
const toSupportedCountry = (code: string | undefined): CountryCode | undefined => {
    const upperCased = code?.toUpperCase();
    return upperCased && isSupportedCountry(upperCased) ? (upperCased as CountryCode) : undefined;
};

const countryFromTimezone = (): CountryCode | undefined => {
    try {
        return toSupportedCountry(TIMEZONE_COUNTRY[Intl.DateTimeFormat().resolvedOptions().timeZone]);
    } catch {
        // Intl is unavailable or the environment reports no timezone. Fall through to the locale.
        return undefined;
    }
};

const countryFromLocales = (locales: string[]): CountryCode | undefined => {
    for (const locale of locales) {
        // A BCP 47 tag can carry a script subtag ('zh-Hans-CN'), so take the first two-letter
        // subtag after the language rather than assuming the region is in second position.
        // Numeric UN M49 regions ('es-419') are skipped, they are not countries.
        const region = locale
            .split('-')
            .slice(1)
            .find((subtag) => /^[a-z]{2}$/i.test(subtag));
        const country = toSupportedCountry(region);
        if (country) {
            return country;
        }
    }
    return undefined;
};

/**
 * Resolves, in order:
 *   1. browser timezone       `America/Sao_Paulo` -> BR
 *   2. locale region subtag   `pt-BR`             -> BR
 *   3. `DEFAULT_PHONE_COUNTRY`
 *
 * This is only ever a default. The user can change it, and an already-saved number always wins,
 * so a wrong guess costs one click rather than producing a wrong number.
 *
 * @param localeHint the language the user picked in Swonkie, tried before the browser's own
 *                   list. Only 'pt-br' carries a region among our locales, the rest fall through.
 */
export const detectPhoneCountry = (localeHint?: string): CountryCode => {
    const browserLocales =
        typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
    const locales = [localeHint, ...browserLocales].filter((locale): locale is string => Boolean(locale));

    return countryFromTimezone() ?? countryFromLocales(locales) ?? DEFAULT_PHONE_COUNTRY;
};
